import { useCallback, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { DashboardConfig } from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { actionError } from '../../components/actionError.ts';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import { LoadingRow } from '../../components/LoadingRow.tsx';
import { useLoadState } from '../../hooks/useLoadState.ts';
import { palette } from '../../theme.ts';
import {
  bpPercentHint,
  buildSectionConfigDiff,
  CONFIG_SECTION_FIELDS,
  FIELD_LABELS,
  formStateFromConfig,
  validateConfigSection,
  validateField,
  type AssertTrue,
  type BpField,
  type ConfigFormState,
  type ConfigSection,
  type EditableField,
} from './configValidation.ts';

// Explicit distribution subset so each section maps its own fields without
// changing BP_FIELDS or per-field props.
const TAX_SPLIT_FIELDS = [
  'tax_split_ngcb_gaming_levy_bp',
  'tax_split_ngcb_lottery_dividend_bp',
  'tax_split_ngcb_community_benefit_bp',
  'tax_split_irc_bp',
] as const satisfies readonly BpField[];

// Every BP_FIELDS key must be placed in one of the section cards below.
// EveryBpFieldRendered fails to compile if a bp field is added to BP_FIELDS but
// not listed in RENDERED_BP_FIELDS (the tuple wrap stops union distribution from
// hiding a missing key). Type-only, so it carries no runtime cost.
const RENDERED_BP_FIELDS = ['tax_rate_bp', ...TAX_SPLIT_FIELDS, 'commission_rate_bp'] as const;
type EveryBpFieldRendered = AssertTrue<[BpField] extends [(typeof RENDERED_BP_FIELDS)[number]] ? true : false>;

export function ConfigPage(): React.JSX.Element {
  const { api } = useAuth();
  const [config, setConfig] = useState<DashboardConfig | null>(null);
  const [form, setForm] = useState<ConfigFormState | null>(null);
  const [saveError, setSaveError] = useState('');
  const [busySection, setBusySection] = useState<ConfigSection | null>(null);
  const [savedOpen, setSavedOpen] = useState(false);

  // The fetcher populates config/form itself (rather than going through
  // useLoadState's own `data`) so a successful load sets both in the SAME
  // render, exactly as the hand-rolled effect this replaces used to --
  // `load.kind` is the only thing this page reads back from the hook.
  const fetchConfig = useCallback(async (): Promise<void> => {
    const data = await api.config.get();
    setConfig(data);
    setForm(formStateFromConfig(data));
  }, [api]);
  const { state: load } = useLoadState<void>(fetchConfig, undefined);

  function setField(field: EditableField, value: string) {
    setSaveError('');
    setForm((f) => (f ? { ...f, [field]: value } : f));
  }

  // One BP TextField render shared by all sections; `f` is the narrowed
  // non-null form so each caller passes the same value it already guarded.
  function renderBpField(field: BpField, f: ConfigFormState) {
    return (
      <TextField
        key={field}
        label={FIELD_LABELS[field]}
        value={f[field]}
        onChange={(e) => setField(field, e.target.value)}
        fullWidth
        disabled={busySection !== null}
        error={validateField(field, f[field]) !== null}
        helperText={validateField(field, f[field]) ?? bpPercentHint(f[field]) ?? ' '}
        slotProps={{ htmlInput: { 'aria-label': field, inputMode: 'numeric' } }}
      />
    );
  }

  function sectionIsDirty(section: ConfigSection): boolean {
    if (!form || !config) return false;
    const baseline = formStateFromConfig(config);
    return CONFIG_SECTION_FIELDS[section].some((field) => form[field] !== baseline[field]);
  }

  function restoreSection(section: ConfigSection, updated: DashboardConfig): void {
    const updatedForm = formStateFromConfig(updated);
    setForm((current) => {
      if (!current) return updatedForm;
      const next = { ...current };
      for (const field of CONFIG_SECTION_FIELDS[section]) next[field] = updatedForm[field];
      return next;
    });
  }

  async function save(section: ConfigSection) {
    if (!form || !config || busySection !== null) return;
    const problem = validateConfigSection(form, section);
    if (problem) {
      // The offending field already shows this exact message as its helper
      // text (live validation below), so the banner stays generic rather
      // than duplicating it.
      setSaveError('Fix the highlighted field before saving.');
      return;
    }
    const diff = buildSectionConfigDiff(config, form, section);
    if (Object.keys(diff).length === 0) {
      restoreSection(section, config);
      setSavedOpen(true);
      return;
    }
    setBusySection(section);
    setSaveError('');
    try {
      const updated = await api.config.update(diff);
      setConfig(updated);
      restoreSection(section, updated);
      setSavedOpen(true);
    } catch (e) {
      setSaveError(actionError(e));
    } finally {
      setBusySection(null);
    }
  }

  function renderSaveButton(section: ConfigSection, label: string) {
    return (
      <Button
        aria-label={`Save ${label}`}
        onClick={() => void save(section)}
        disabled={busySection !== null || !sectionIsDirty(section)}
      >
        Save
      </Button>
    );
  }

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Typography variant="h5" gutterBottom>
        Config
      </Typography>

      {load.kind === 'loading' && (
        <Box sx={{ mt: 2 }}>
          <LoadingRow label="Loading config..." />
        </Box>
      )}

      {load.kind === 'error' && (
        <Box sx={{ mt: 2 }}>
          <LoadErrorAlert message={load.message} />
        </Box>
      )}

      {load.kind === 'ready' && form && (
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* alignItems:start so shorter cards keep their natural height and do not stretch to the tallest sibling. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Taxation
              </Typography>
              <Stack spacing={2.5}>
                <TextField
                  label="Tax basis"
                  value="Gross profit"
                  fullWidth
                  disabled
                  slotProps={{ htmlInput: { 'aria-label': 'tax-basis' } }}
                />
                {renderBpField('tax_rate_bp', form)}
                <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                  How the tax rate is split across the funds:
                </Typography>
                {TAX_SPLIT_FIELDS.map((field) => renderBpField(field, form))}
                <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                  Tax rate and split changes apply to reports and summaries immediately.
                </Typography>
                <Box>{renderSaveButton('taxation', 'Taxation')}</Box>
              </Stack>
            </Paper>

            <Stack spacing={3}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  1NL billing
                </Typography>
                <Stack spacing={2.5}>
                  <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                    These are the defaults. Any shop can override either value on its own page.
                  </Typography>
                  {renderBpField('commission_rate_bp', form)}
                  <TextField
                    label={FIELD_LABELS.device_monthly_fee_minor}
                    value={form.device_monthly_fee_minor}
                    onChange={(e) => setField('device_monthly_fee_minor', e.target.value)}
                    fullWidth
                    disabled={busySection !== null}
                    error={validateField('device_monthly_fee_minor', form.device_monthly_fee_minor) !== null}
                    helperText={
                      validateField('device_monthly_fee_minor', form.device_monthly_fee_minor) ??
                      'Charged for each device, every month. Enter an amount like 1000.00.'
                    }
                    slotProps={{ htmlInput: { 'aria-label': 'device_monthly_fee_minor', inputMode: 'decimal' } }}
                  />
                  <Box>{renderSaveButton('billing', '1NL billing')}</Box>
                </Stack>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Licensing
                </Typography>
                <Stack spacing={2.5}>
                  <TextField
                    label={FIELD_LABELS.licence_validity_days}
                    value={form.licence_validity_days}
                    onChange={(e) => setField('licence_validity_days', e.target.value)}
                    fullWidth
                    disabled={busySection !== null}
                    error={validateField('licence_validity_days', form.licence_validity_days) !== null}
                    helperText={
                      validateField('licence_validity_days', form.licence_validity_days) ??
                      "Takes effect at each shop's next sync."
                    }
                    slotProps={{ htmlInput: { 'aria-label': 'licence_validity_days', inputMode: 'numeric' } }}
                  />
                  <Box>{renderSaveButton('licensing', 'Licensing')}</Box>
                </Stack>
              </Paper>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Sync endpoint
                </Typography>
                <Stack spacing={2.5}>
                  <TextField
                    label={FIELD_LABELS.sync_endpoint_url}
                    value={form.sync_endpoint_url}
                    onChange={(e) => setField('sync_endpoint_url', e.target.value)}
                    fullWidth
                    disabled={busySection !== null}
                    error={validateField('sync_endpoint_url', form.sync_endpoint_url) !== null}
                    helperText={
                      validateField('sync_endpoint_url', form.sync_endpoint_url) ??
                      'Leave empty except during an API migration. When set, every shop is told to move to this URL at its next sync.'
                    }
                    slotProps={{
                      htmlInput: { 'aria-label': 'sync_endpoint_url' },
                    }}
                  />
                  <Box>{renderSaveButton('sync', 'Sync endpoint')}</Box>
                </Stack>
              </Paper>
            </Stack>
          </Box>

          {saveError && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
              {saveError}
            </Alert>
          )}
        </Stack>
      )}

      <Snackbar open={savedOpen} autoHideDuration={3000} onClose={() => setSavedOpen(false)} message="Config saved." />
    </Box>
  );
}

export default ConfigPage;
